import React, { useState, useCallback, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";
import { useTVNavigation } from "./hooks/useTVNavigation";
import "./Rosario.scss";

const Letanias = ({ isTV = false }) => {
  const { t } = useTranslation();

  // Hook de navegación para TV
  useTVNavigation(isTV);

  // Estado: 0 = intro, 1-54 = invocaciones, 55+ = completado
  const [step, setStep] = useState(0);

  // Estructura de las Letanías Lauretanas - envuelto en useMemo
  const litaniesStructure = useMemo(() => [
    // Señor, ten piedad (x3)
    { type: "lord_mercy", repeat: 3 },
    // Santa María (intro)
    { type: "holy_mary" },
    // Madre de Cristo (3 invocaciones)
    { type: "mother_christ", repeat: 3 },
    // 3 Señor, ten piedad
    { type: "lord_mercy_short", repeat: 3 },
    // 21 invocaciones de la Virgen
    { type: "virgin_invocations" },
    // 3 Señor, ten piedad
    { type: "lord_mercy_short", repeat: 3 },
    // 4 invocaciones más
    { type: "more_invocations" },
    // 3 Señor, ten piedad
    { type: "lord_mercy_short", repeat: 3 },
    // 13 invocaciones finales
    { type: "final_invocations" },
    // Oración final
    { type: "final_prayer" }
  ], []);

  // Invocaciones de la Virgen María - envuelto en useMemo
  const virginInvocations = useMemo(() => [
    "mother_of_christ",
    "mother_of_divine_grace",
    "mother_most_pure",
    "mother_most_chaste",
    "mother_ever_virgin",
    "mother_immaculate",
    "mother_of_the_church",
    "mother_of_mercy",
    "virgin_most_prudent",
    "virgin_most_venerable",
    "virgin_most_praiseworthy",
    "virgin_most_powerful",
    "virgin_most_clement",
    "virgin_most_faithful",
    "mirror_of_justice",
    "seat_of_wisdom",
    "cause_of_our_joy",
    "vessel_of_honor",
    "singular_vessel_of_devotion",
    "mystical_rose",
    "tower_of_david",
    "tower_of_ivory",
    "house_of_gold",
    "ark_of_the_covenant",
    "gate_of_heaven",
    "morning_star",
    "health_of_the_sick",
    "refuge_of_sinners",
    "comforter_of_the_afflicted",
    "help_of_christians",
    "queen_of_angels",
    "queen_of_patriarchs",
    "queen_of_prophets",
    "queen_of_apostles",
    "queen_of_martyrs",
    "queen_of_confessors",
    "queen_of_virgins",
    "queen_of_all_saints",
    "queen_conceived_without_sin",
    "queen_assumed_into_heaven",
    "queen_of_the_most_holy_rosary"
  ], []);

  const moreInvocations = useMemo(() => [
    "queen_of_the_most_holy_rosary",
    "mother_of_the_church",
    "mother_of_mercy",
    "mirror_of_justice",
    "seat_of_wisdom",
    "cause_of_our_joy",
    "vessel_of_honor",
    "singular_vessel_of_devotion",
    "mystical_rose"
  ], []);

  const finalInvocations = useMemo(() => [
    "queen_of_angels",
    "queen_of_patriarchs",
    "queen_of_prophets",
    "queen_of_apostles",
    "queen_of_martyrs",
    "queen_of_confessors",
    "queen_of_virgins",
    "queen_of_all_saints",
    "queen_conceived_without_sin",
    "queen_assumed_into_heaven",
    "queen_of_the_most_holy_rosary",
    "mother_of_the_church",
    "mother_of_mercy",
    "mirror_of_justice",
    "seat_of_wisdom",
    "cause_of_our_joy"
  ], []);

  // Calcular índice actual en la estructura plana
  const getInvocationInfo = useCallback(() => {
    let totalCount = 0;

    for (const section of litaniesStructure) {
      if (section.type === "virgin_invocations") {
        for (let i = 0; i < virginInvocations.length; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t(`litany.${virginInvocations[i]}`),
              response: t("litany.pray_for_us"),
              isMary: true
            };
          }
        }
      } else if (section.type === "more_invocations") {
        for (let i = 0; i < moreInvocations.length; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t(`litany.${moreInvocations[i]}`),
              response: t("litany.pray_for_us"),
              isMary: true
            };
          }
        }
      } else if (section.type === "final_invocations") {
        for (let i = 0; i < finalInvocations.length; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t(`litany.${finalInvocations[i]}`),
              response: t("litany.pray_for_us"),
              isMary: true
            };
          }
        }
      } else if (section.type === "holy_mary") {
        totalCount++;
        if (totalCount === step) {
          return {
            title: t("litany.holy_mary"),
            response: t("litany.pray_for_us"),
            isMary: true
          };
        }
      } else if (section.type === "final_prayer") {
        totalCount++;
        if (totalCount === step) {
          return {
            title: t("litany.final_prayer"),
            isFinal: true
          };
        }
      } else if (section.type === "lord_mercy") {
        for (let i = 0; i < section.repeat; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t("litany.lord_have_mercy"),
              response: t("litany.lord_have_mercy_response"),
              isLord: true
            };
          }
        }
      } else if (section.type === "lord_mercy_short") {
        for (let i = 0; i < section.repeat; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t("litany.lord_have_mercy"),
              response: t("litany.lord_have_mercy_response"),
              isLord: true
            };
          }
        }
      } else if (section.type === "mother_christ") {
        totalCount++;
        if (totalCount === step) {
          return {
            title: t("litany.mother_of_christ"),
            response: t("litany.pray_for_us"),
            isMary: true
          };
        }
      }
    }
    return null;
  }, [step, t, finalInvocations, litaniesStructure, moreInvocations, virginInvocations]);

  const currentInvocation = getInvocationInfo();
  const totalSteps = 54; // 54 invocaciones principales + oración final

  // Handlers
  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleReset = () => {
    setStep(0);
  };

  // Función auxiliar para manejar eventos de teclado en modo TV
  const handleKeyPress = (callback) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback(e);
    }
  };

  // Props para elementos interactivos en modo TV
  const tvInteractiveProps = isTV ? {
    tabIndex: 0,
    role: 'button'
  } : {};

  // ===== VISTA DE INICIO =====
  if (step === 0) {
    return (
      <div className="rosario-simple">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="mb-3">
                <h1 className="litany-title mb-2">{t("litany.title")}</h1>
                <p className="text-muted lead">{t("litany.subtitle")}</p>
              </div>

              <Card className="text-center mystery-preview mb-4">
                <Card.Body>
                  <div className="mystery-icon-large">🙏</div>
                  <h3 className="mt-3">{t("litany.holy_mary")}</h3>
                  <p className="text-muted mt-3">
                    {t("litany.description")}
                  </p>
                </Card.Body>
              </Card>

              <div className="text-center mb-4">
                <p className="text-muted">
                  <small>📖 {t("litany.origin")}</small>
                </p>
              </div>

              <Card className="instructions-card mb-4">
                <Card.Body>
                  <p className="mb-0">{t("litany.instructions")}</p>
                </Card.Body>
              </Card>

              <Button
                variant="primary"
                size="lg"
                className="w-100 py-3 start-button"
                onClick={handleNext}
                {...tvInteractiveProps}
                onKeyDown={handleKeyPress(handleNext)}
              >
                {t("coronilla.start")} →
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE COMPLETADO =====
  if (step > totalSteps) {
    return (
      <div className="rosario-simple completion">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="completion-icon">🙏</div>
              <h1 className="mt-4">{t("litany.completed.title")}</h1>
              <p className="lead">{t("litany.completed.message")}</p>
              <p className="text-muted mb-4">{t("litany.completed.blessing")}</p>

              <Card className="completion-prayer mb-4">
                <Card.Body>
                  <p>{t("litany.final_prayer")}</p>
                </Card.Body>
              </Card>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleReset}
                  {...tvInteractiveProps}
                  onKeyDown={handleKeyPress(handleReset)}
                >
                  {t("litany.actions.pray_again")}
                </Button>
                <Button
                  variant="outline-secondary"
                  href="/"
                  {...tvInteractiveProps}
                >
                  {t("rosary.actions.home")}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE LETANÍAS =====
  // Calcular progreso
  const progress = Math.min((step / totalSteps) * 100, 100);

  return (
    <div className="rosario-simple prayer-view">
      <Container className="rosario-container">
        <div className="prayer-content-wrapper">
          {/* Header */}
          <div className="rosario-header mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Button
                variant="link"
                className="text-muted p-0"
                onClick={handleReset}
                {...tvInteractiveProps}
                onKeyDown={handleKeyPress(handleReset)}
              >
                ← {t("rosary.actions.back")}
              </Button>
              <div className="decade-counter">
                <span className="decade-current">{step}</span>
                <span className="decade-separator">/</span>
                <span className="decade-total">{totalSteps + 1}</span>
              </div>
            </div>
            <div className="mystery-label text-center">
              {t("litany.title")}
            </div>
          </div>

          {/* Barra de progreso */}
          <div className="decade-progress-container mb-4">
            <div
              className="decade-segment active"
              style={{ width: `${progress}%` }}
            >
              <div className="decade-segment-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* Tarjeta de oración */}
          <Card
            className="prayer-card-main"
            onClick={(e) => {
              if (!e.target.closest('button')) handleNext();
            }}
          >
            <Card.Body className="prayer-card-body">
              <div className="prayer-content">
                {currentInvocation.isFinal ? (
                  <>
                    <h2 className="prayer-title-main mb-4">
                      {t("rosary.prayers.holy_queen")}
                    </h2>
                    <p className="prayer-text-main lead" dangerouslySetInnerHTML={{ __html: currentInvocation.title }} />
                  </>
                ) : (
                  <>
                    <h2 className="prayer-title-main mb-4">
                      {currentInvocation.isLord ? t("litany.christ_have_mercy") : t("litany.holy_mary")}
                    </h2>
                    <p className="prayer-text-main lead mb-3">
                      {currentInvocation.title}
                    </p>
                    <p className="prayer-response lead text-muted">
                      <strong>{currentInvocation.response}</strong>
                    </p>
                    <div className="tap-indicator mt-4 text-muted">
                      <small>{t("rosary.simple.tap_continue")}</small>
                    </div>
                  </>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Controles */}
          <div className="navigation-controls d-flex justify-content-between gap-2 mt-4">
            <Button
              variant="outline-secondary"
              className="nav-btn flex-grow-1"
              onClick={handlePrevious}
              disabled={step === 1}
              {...tvInteractiveProps}
              onKeyDown={handleKeyPress(handlePrevious)}
            >
              ← {t("rosary.simple.previous")}
            </Button>
            <Button
              variant="primary"
              className="nav-btn flex-grow-1"
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
              {...tvInteractiveProps}
              onKeyDown={handleKeyPress((e) => { e.stopPropagation(); handleNext(); })}
            >
              {t("rosary.simple.next")} →
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Letanias;
